import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { FadeUp } from "@/components/site/Reveal";
import TrialCta from "@/components/site/TrialCta";
import { useAuth } from "@/context/AuthContext";
import { PLANS, PLAN_FEATURES } from "@/config";

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;
const MONTHLY = 999;

const Pricing = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const buy = (plan) => {
        navigate(user ? `/checkout?plan=${plan.duration_months}` : `/register?plan=${plan.duration_months}`);
    };

    return (
    <section id="membership" data-testid="membership-section" className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20">
        <FadeUp>
            <p className="eyebrow">Membership</p>
            <h2 className="font-display mt-4 text-4xl leading-[1.08] text-white sm:text-5xl lg:text-6xl">
                Pick your commitment.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#9fb0c7]" data-testid="membership-trial-note">
                Creating your SHREDxpro account is free. Try the optional 7-day free trial above —
                or purchase a membership directly below. Every plan includes every feature; the only
                difference is how long you commit, and how much you save.
            </p>
        </FadeUp>

        <FadeUp delay={0.05}>
            <div
                className="card-surface mt-12 flex flex-col items-center gap-5 border-[#3fcbf3]/25 p-8 text-center sm:p-10"
                data-testid="pricing-trial-banner"
            >
                <div className="flex flex-wrap justify-center gap-2">
                    {["7 Days Free", "Full SHREDxpro Access", "No Payment Required"].map((c) => (
                        <span key={c} className="chip">
                            <Check className="h-3.5 w-3.5 text-[#3fcbf3]" />
                            {c}
                        </span>
                    ))}
                </div>
                <TrialCta />
                <p className="text-xs text-[#7d8da3]">
                    The free trial never opens a payment gateway — no Google Play, App Store or
                    Razorpay transaction.
                </p>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#5c6b82]">
                    Or pick a membership below
                </p>
            </div>
        </FadeUp>

        <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {PLANS.map((plan, i) => {
                const savePct = Math.round((1 - plan.price / (MONTHLY * plan.duration_months)) * 100);
                return (
                    <FadeUp key={plan.duration_months} delay={i * 0.07} className="h-full">
                        <div
                            data-testid={`pricing-card-${plan.duration_months}`}
                            className={`h-full rounded-3xl ${
                                plan.is_featured
                                    ? "bg-gradient-to-b from-[#45d1f5] to-[#f0356f] p-[1.5px] shadow-[0_0_50px_rgba(63,203,243,0.15)]"
                                    : "border border-white/[0.08]"
                            }`}
                        >
                            <div className={`relative flex h-full flex-col rounded-3xl bg-[#0b1422] p-7 ${plan.is_featured ? "" : "transition-colors duration-300 hover:border-transparent"}`}>
                                {plan.is_featured && (
                                    <span
                                        data-testid="pricing-best-value-badge"
                                        className="absolute -top-3 left-6 rounded-full bg-[#3fcbf3] px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#07101c]"
                                    >
                                        Best Value
                                    </span>
                                )}
                                <p className="font-display text-2xl text-white">{plan.label}</p>
                                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-[#7d8da3]">{plan.tagline}</p>
                                <div className="mt-6 flex items-end gap-2">
                                    <span className="font-display text-5xl text-white">{inr(plan.price)}</span>
                                    <span className="pb-1.5 text-xs text-[#7d8da3]">
                                        ≈ {inr(Math.round(plan.price / plan.duration_months))}/mo
                                    </span>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    {savePct > 0 && (
                                        <span
                                            data-testid={`pricing-save-${plan.duration_months}`}
                                            className="rounded-full border border-[#f0356f]/40 bg-[#f0356f]/10 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#f0356f]"
                                        >
                                            Save {savePct}%
                                        </span>
                                    )}
                                </div>
                                <p className="mt-6 flex-1 text-sm leading-relaxed text-[#9fb0c7]">
                                    Full access to everything SHREDxpro offers, billed every{" "}
                                    {plan.duration_months === 1 ? "month" : `${plan.duration_months} months`}.
                                </p>
                                <button
                                    onClick={() => buy(plan)}
                                    data-testid={`pricing-cta-${plan.duration_months}`}
                                    className={`mt-8 w-full ${plan.is_featured ? "btn-primary" : "btn-ghost"}`}
                                >
                                    Buy Now
                                </button>
                            </div>
                        </div>
                    </FadeUp>
                );
            })}
        </div>

        <FadeUp delay={0.1}>
            <div className="card-surface mt-10 rounded-2xl p-6 sm:p-8" data-testid="membership-features-note">
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-white">
                    Every plan includes full access
                </p>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {PLAN_FEATURES.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-sm text-[#c8d3e2]">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#3fcbf3]" />
                            {f}
                        </li>
                    ))}
                </ul>
            </div>
        </FadeUp>

        <FadeUp delay={0.15}>
            <div className="card-surface mt-6 rounded-2xl p-6 sm:p-8" data-testid="membership-billing-note">
                <p className="text-sm leading-relaxed text-[#9fb0c7]">
                    <span className="font-bold text-white">How billing works.</span> Creating an
                    account on this website is free. Memberships are purchased directly through
                    secure online checkout as a one-time payment for the full plan duration, and
                    activate on your account immediately after payment — the same account unlocks
                    the SHREDxpro app on iOS and Android. No hidden fees, no lock-in contracts.
                </p>
            </div>
        </FadeUp>
    </section>
    );
};

export default Pricing;
